import {
  faixaDoGiro,
  type EixoColuna,
  type FaixaGiro,
} from '../geometria/anguloExtracao'
import { comprimento, subtrair, type Vetor3 } from '../geometria/vetor3'
import type { ProporcoesVitima } from './perfisVitima'
import {
  FIM_ROTACAO,
  FRACAO_TORCAO_PADRAO,
  amostrarRota,
  type OpcoesRota,
  type RotaExtracao,
} from './rotasExtracao'
import { posicaoCabeca, posicaoOmbros, type ColocacaoVitima } from './rigVitima'

/**
 * Quanto a rota custa à vítima, em movimento executado sobre ela.
 *
 * O ângulo sozinho é uma categoria: diz 30° ou 90°, não diz o que isso significa
 * para quem está no banco. Este módulo converte a mesma rota em grandezas que o
 * aluno sente — centímetros de arco percorridos pelos ombros, graus de torção
 * sobrando na cervical — para sustentar o princípio de evitar o giro.
 *
 * Por que os OMBROS e não a cabeça: `POSE_SENTADA` tem `troncoInclinacaoRad`
 * zero de propósito (`rigVitima.ts`), então o eixo da coluna da vítima sentada é
 * exatamente +Y e a cabeça fica praticamente SOBRE o eixo da guinada. Medir o
 * arco na cabeça daria perto de zero em qualquer ângulo. Os ombros varrem o arco
 * de verdade, e já são a referência de folga que o projeto usa. `arcoCabecaCm`
 * continua calculado porque, com a vítima reclinada sobre a prancha ou deitada no
 * teto do capotado, a cabeça sai do eixo e o número volta a ter conteúdo.
 *
 * NÃO é medida clínica nem biomecânica: é a geometria da manobra que a própria
 * cena executa, lida em números.
 */

/** Amostras usadas para medir o caminho percorrido durante a fase de rotação. */
const AMOSTRAS = 24

export interface CustoDoGiro {
  /** giro do corpo em torno da vertical, em graus, sempre positivo */
  giroGraus: number
  /**
   * Caminho varrido pela linha dos ombros durante a rotação, em centímetros,
   * com o corpo movido em bloco — o movimento que a rota exige da vítima.
   */
  arcoOmbrosCm: number
  /** idem para o centro da cabeça: ≈0 sentada, relevante deitada ou reclinada */
  arcoCabecaCm: number
  /** parcela do giro que sobra como torção entre pelve e cabeça, em graus */
  torcaoCervicalGraus: number
  faixa: FaixaGiro
}

const grausDe = (radianos: number): number => Math.abs((radianos * 180) / Math.PI)

/**
 * Caminho percorrido por um ponto do corpo durante a fase de rotação.
 *
 * Medido EM RELAÇÃO À PELVE, não em coordenadas de mundo: durante a rotação o
 * corpo também avança pela rota (`AVANCO_DURANTE_ROTACAO`), e somar esse avanço
 * mediria translação, não giro.
 */
function caminhoRelativo(
  rota: RotaExtracao,
  eixoColuna: EixoColuna,
  colocacaoInicial: ColocacaoVitima,
  proporcoes: ProporcoesVitima,
  opcoes: OpcoesRota,
  ponto: (colocacao: ColocacaoVitima, pose: ReturnType<typeof amostrarRota>['pose']) => Vetor3,
): number {
  let total = 0
  let anterior: Vetor3 | null = null

  // o arco é amostrado com o corpo EM BLOCO, qualquer que seja a torção pedida:
  // ele mede o movimento que a rota exige da vítima, não como esse movimento é
  // repartido entre pelve, tronco e cabeça. Com parte do giro na cervical, os
  // ombros giram menos que o total (a cabeça completa o resto) e o arco encolhia
  // — o que diria que torcer o pescoço "move menos a vítima", exatamente a
  // leitura errada. A torção tem sua própria linha no resultado
  const emBloco: OpcoesRota = { ...opcoes, fracaoTorcao: 0 }

  for (let i = 0; i <= AMOSTRAS; i += 1) {
    const t = (i / AMOSTRAS) * FIM_ROTACAO
    const amostra = amostrarRota(rota, eixoColuna, t, emBloco)
    // a mesma composição que a cena faz: giro e reclinação SOMAM-SE à colocação
    const colocacao: ColocacaoVitima = {
      posicaoPelve: colocacaoInicial.posicaoPelve,
      guinadaRad: colocacaoInicial.guinadaRad + amostra.guinadaRad,
      reclinacaoRad: (colocacaoInicial.reclinacaoRad ?? 0) + amostra.reclinacaoRad,
    }
    const atual = ponto(colocacao, amostra.pose)
    if (anterior) total += comprimento(subtrair(atual, anterior))
    anterior = atual
  }

  return total
}

export function custoDoGiro(
  rota: RotaExtracao,
  eixoColuna: EixoColuna,
  colocacaoInicial: ColocacaoVitima,
  proporcoes: ProporcoesVitima,
  opcoes: OpcoesRota = {},
): CustoDoGiro {
  const giroGraus = grausDe(rota.giro.anguloRad)

  const arcoOmbros = caminhoRelativo(
    rota,
    eixoColuna,
    colocacaoInicial,
    proporcoes,
    opcoes,
    (colocacao, pose) => posicaoOmbros(colocacao, proporcoes, pose).esquerdo,
  )
  const arcoCabeca = caminhoRelativo(
    rota,
    eixoColuna,
    colocacaoInicial,
    proporcoes,
    opcoes,
    (colocacao, pose) => posicaoCabeca(colocacao, proporcoes, pose),
  )

  return {
    giroGraus,
    arcoOmbrosCm: arcoOmbros * 100,
    arcoCabecaCm: arcoCabeca * 100,
    // a mesma fração que `amostrarRota` aplica ao boneco: o número do painel e o
    // pescoço torcido na tela não podem sair de constantes diferentes
    torcaoCervicalGraus: giroGraus * (opcoes.fracaoTorcao ?? FRACAO_TORCAO_PADRAO),
    faixa: faixaDoGiro(giroGraus),
  }
}
